import { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Calendar, LayoutGrid, Columns, GitBranch, FileText, ArrowRight } from 'lucide-react';
import { QtplanLogo } from '../components/QtplanLogo';
import { ActiveBoardProvider } from '../boardv3/stores/ActiveBoardContext';
import { setupPlaygroundBoard, PLAYGROUND_BOARD_ID } from './playgroundData';
import { boardsStore } from '../stores/boardsStore';
import { LandingDemoContent, type DemoView } from './LandingDemoContent';

const DEMO_VIEWS = [
  { key: 'roadmap' as const, labelKey: 'nav.roadmap' as const, icon: Calendar },
  { key: 'overview' as const, labelKey: 'nav.overview' as const, icon: LayoutGrid },
  { key: 'columns' as const, labelKey: 'nav.columns' as const, icon: Columns },
  { key: 'canvas' as const, labelKey: 'nav.canvas' as const, icon: GitBranch },
  { key: 'editor' as const, labelKey: 'nav.editor' as const, icon: FileText },
];

export function LandingDemo() {
  const { t } = useTranslation();
  const [ready, setReady] = useState(false);
  const [view, setView] = useState<DemoView>('roadmap');
  const navigate = useNavigate();

  useEffect(() => {
    const prevBoardId = boardsStore.getSnapshot().context.activeBoardId;
    setupPlaygroundBoard();
    boardsStore.send({ type: 'board.setActive', boardId: PLAYGROUND_BOARD_ID });
    setReady(true);

    return () => {
      if (prevBoardId && prevBoardId !== PLAYGROUND_BOARD_ID) {
        boardsStore.send({ type: 'board.setActive', boardId: prevBoardId });
      }
    };
  }, []);

  if (!ready) return null;

  return (
    <div className="landing-demo-shell">
      <nav className="landing-demo-sidebar">
        <div className="landing-demo-sidebar-header">
          <QtplanLogo size={16} className="text-gray-900" />
          <span className="landing-demo-sidebar-title">qtplan</span>
        </div>
        <div className="landing-demo-sidebar-board">Playground</div>
        <div className="landing-demo-sidebar-nav">
          {DEMO_VIEWS.map((v) => (
            <button
              key={v.key}
              className={`landing-demo-sidebar-item ${view === v.key ? 'active' : ''}`}
              onClick={() => setView(v.key)}
            >
              <v.icon size={16} strokeWidth={1.5} />
              <span>{t(v.labelKey)}</span>
            </button>
          ))}
        </div>
        <div className="landing-demo-sidebar-spacer" />
        <button className="landing-demo-sidebar-cta" onClick={() => navigate({ to: '/boards' })}>
          {t('landing.createOwnBoard')}
          <ArrowRight size={14} />
        </button>
      </nav>

      <div className="landing-demo-main">
        <ActiveBoardProvider boardId={PLAYGROUND_BOARD_ID}>
          <LandingDemoContent view={view} />
        </ActiveBoardProvider>
      </div>
    </div>
  );
}
